import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";

const TONE = {
  success: { color: "#047857", bg: "#D1FAE5", ring: "#10B981" },
  info: { color: "#0052FF", bg: "#DBEAFE", ring: "#3B82F6" },
  warning: { color: "#B45309", bg: "#FEF3C7", ring: "#F59E0B" },
  danger: { color: "#B91C1C", bg: "#FEE2E2", ring: "#EF4444" },
};

function ScoreRing({ score, ringColor }) {
  // Circular progress (0-100). r=52, C = 2*pi*r ≈ 326.7
  const r = 52;
  const C = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dash = C * pct;
  return (
    <div className="relative w-[140px] h-[140px] flex items-center justify-center shrink-0">
      <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
        <circle
          cx="70" cy="70" r={r}
          stroke="#E2E8F0" strokeWidth="10" fill="none"
        />
        <circle
          cx="70" cy="70" r={r}
          stroke={ringColor} strokeWidth="10" fill="none"
          strokeDasharray={`${dash} ${C - dash}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 600ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-display text-4xl font-bold tracking-tight font-numeric"
          style={{ color: ringColor }}
          data-testid="finly-score-value"
        >
          {score}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
          / 100
        </span>
      </div>
    </div>
  );
}

function TrendBadge({ delta, trend }) {
  if (trend === "up") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold font-numeric"
        style={{ backgroundColor: "#D1FAE5", color: "#047857" }}
        data-testid="finly-score-trend"
      >
        <TrendingUp className="w-3.5 h-3.5" />
        ↑ {Math.abs(delta)} pts
      </span>
    );
  }
  if (trend === "down") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold font-numeric"
        style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}
        data-testid="finly-score-trend"
      >
        <TrendingDown className="w-3.5 h-3.5" />
        ↓ {Math.abs(delta)} pts
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold font-numeric"
      style={{ backgroundColor: "#F1F5F9", color: "#64748B" }}
      data-testid="finly-score-trend"
    >
      <Minus className="w-3.5 h-3.5" />
      Sin cambio
    </span>
  );
}

function ComponentBar({ comp, ringColor }) {
  const width = Math.max(0, Math.min(100, comp.score));
  return (
    <div className="space-y-1.5" data-testid={`finly-score-comp-${comp.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-[#F1F5F9] truncate">
          {comp.label}
          <span className="text-[10px] text-[#94A3B8] ml-1.5 font-numeric">
            · {comp.weight}%
          </span>
        </span>
        <span className="text-xs font-semibold font-numeric text-[#F1F5F9]">
          {comp.score}
        </span>
      </div>
      <div className="h-1.5 bg-[#1B2638] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${width}%`, backgroundColor: ringColor }}
        />
      </div>
      <p className="text-[11px] text-[#64748B] leading-snug">{comp.hint}</p>
    </div>
  );
}

export default function FinlyScoreCard({ month, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api
      .get(`/finly-score?month=${month}&year=${year}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const tone = (data && TONE[data.tone]) || TONE.info;

  return (
    <div className="clay-card p-6 fade-up" data-testid="finly-score-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${tone.ring}22` }}
          >
            <Gauge className="w-4 h-4" style={{ color: tone.ring }} />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-[#F1F5F9] flex items-center gap-2">
              Finly Score
              <Sparkles className="w-4 h-4 text-[#FBBF24]" />
            </h2>
            <p className="text-xs text-[#94A3B8]">
              Tu salud financiera del mes (0–100)
            </p>
          </div>
        </div>
        {data && (
          <span
            className="hidden sm:inline-flex px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.15em]"
            style={{ backgroundColor: tone.bg, color: tone.color }}
            data-testid="finly-score-rating"
          >
            {data.rating}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#94A3B8] py-8 text-center">
          Calculando tu Finly Score…
        </p>
      ) : error || !data ? (
        <p className="text-sm text-[#94A3B8] py-8 text-center">
          No se pudo calcular el Finly Score.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
          <div className="flex flex-col items-center gap-3">
            <ScoreRing score={data.score} ringColor={tone.ring} />
            <TrendBadge delta={data.delta} trend={data.trend} />
            <p className="text-[11px] text-[#94A3B8] text-center font-numeric">
              Mes anterior: {data.previous_score}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {data.components.map((comp) => (
              <ComponentBar key={comp.key} comp={comp} ringColor={tone.ring} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
